<template lang="pug">
  div(class="eds-iit-checkbox")
    h3(
      class="eds-iit-checkbox__title"
      :class="{ 'mb-2': currentStep === steps.diiaSign || currentStep === steps.smartIdRead }"
      data-test="eds-iit-checkbox-title"
    )
      v-btn(
        v-if="backBtnVisible"
        class="eds-iit-checkbox__back-btn"
        @click="handleBackBtnClick"
        :disabled="backButtonDisabledComputed"
        icon
        small
        data-test="eds-iit-checkbox-back-btn"
      )
        e-svg-icon(size="sm") arrow-next
      span {{ $t(shownTitle) }}
    div(
      v-if="isSmartIdOption"
      class="eds-iit-checkbox__page-counter"
      :class="{ 'mb-5': currentStep === steps.smartIdSign }"
    ) {{ $t('Step') }} {{ currentSmartIdStep }} / {{ totalSmartIdSteps.length }}
    div(
      v-if="showSubtitle"
      class="eds-iit-checkbox__subtitle"
    ) {{ subtitle }}
    div(
      v-if="localShowCachedDataMessage"
      class="eds-iit-checkbox__loader"
    )
      p {{  $t("You already downloaded the director's EDS signature as part of the session, so we took it from the cache. Wait for the operation to complete")  }}
      e-progress-circular(
        size="xl"
        width="5"
        color="#161b25"
      )
    div(v-else-if="currentStep === steps.savedKeys")
      eds-iit-saved-keys(
        :end-user="endUser"
        :model-instance="modelInstance"
        :on-interface-action-error="onInterfaceActionError"
        :on-confirm="onConfirm"
        @goToFileSelect="handleOptionSelect(steps.fileSign)"
        @resetLocalData="resetSavedData"
        @resetModelData="saveModelData({ title: null, qrUrl: null })"
        @hideSavedKeysBtn="hideSavedKeysBtn = true"
        @changeStep="handleStepChange"
        @confirm="$emit('confirm', $event)"
      )
    eds-iit-checkbox-sign-options(
      v-else-if="currentStep === steps.options"
      :have-saved-keys="haveSavedKeysWithAuthBoolean"
      :visible-options="visibleSignOptions"
      @select="handleOptionSelect"
    )
    eds-iit-checkbox-key-info(
      v-else-if="currentStep === steps.keyInfo && localKeyData"
      :key-data="localKeyData"
      :loading="loading"
      :show-save-switch="showSaveSwitch"
      :is-stamp="isStamp"
      @confirm="handleConfirm"
    )
    eds-iit-checkbox-form(
      v-else-if="currentStep === steps.fileSign"
      :pkFormType.sync="pkFormType"
      :end-user="endUser"
      :loading="loading"
      :text-error="textError"
      :pk-form-types="pkFormTypes"
      :is-pk-form-type-file="isPkFormTypeFile"
      :is-pk-form-type-k-m="isPkFormTypeKM"
      :is-pk-form-type-k-s-p="isPkFormTypeKSP"
      :initialize-error="initializeError"
      :settings="settings"
      :have-saved-keys="haveSavedKeysWithAuthBoolean"
      :key-medias="keyMedias"
      :cloud-providers="formCloudProviders"
      @readStamp="isStamp = $event"
      @selectCloudProvider="selectedCloudProviderIndex = $event"
      @getLocalKeyData="localKeyData = $event"
      @changeStep="handleStepChange"
      @resetModelData="saveModelData({ qrUrl: null })"
      @resetLocalData="resetSavedData"
      @handleQrCloudSignature="handleQrCloudSignature"
    )
    div(v-else-if="showQrBlock")
      a(
        v-if="qrUrl"
        :href="qrUrl"
        target="_blank"
        class="eds-iit-checkbox__qr-code"
      )
        qrcode-vue(
          :value="qrUrl"
          :size="xsDevice ? 248 : 300"
          class="d-flex justify-center"
        )
        button(
          v-if="xsDevice"
          class="eds-iit-checkbox__button-link"
        )
          span(
            class="d-flex align-center"
            :class="[isSmartIdOption ? 'eds-iit-checkbox__button-link-icon' : 'mr-2']"
          )
            e-svg-icon(:name="buttonLinkIcon")
          span {{ buttonLinkText }}
      div(
        class="eds-iit-checkbox__loader"
        v-else
      )
        e-progress-circular(
          size="xl"
          width="5"
          color="#161b25"
        )
</template>

<script>
import { mapGetters } from 'vuex'
import QrcodeVue from 'qrcode.vue'
import Base64js from 'base64-js'
import { EndUser, EndUserConstants } from 'euscp'
import CAs from 'assets/data/iit/CAs.json'
import EdsIitCheckboxForm from '~/modules/eds-iit-checkbox/components/eds-iit-checkbox-form'
import EdsIitSavedKeys from '~/modules/eds-iit-checkbox/components/eds-iit-saved-keys'
import EdsIitCheckboxKeyInfo from '~/modules/eds-iit-checkbox/components/eds-iit-checkbox-key-info'
import EProgressCircular from '~/components/elements/progress/e-progress-circular'
import EdsIitCheckbox from '~/modules/eds-iit-checkbox/models/EdsIitCheckbox'
import EdsIitCheckboxSignOptions from '~/modules/eds-iit-checkbox/components/eds-iit-checkbox-sign-options'
import edsIitCheckboxSteps from '~/modules/eds-iit-checkbox/mixins/edsIitCheckboxSteps'
import responsive from '~/mixins/pages/responsive'
import allowedKeyMediaTypes from '~/modules/eds-iit-checkbox/constants/allowedKeyMediaTypes'
import cryptoEdsKeys from '~/modules/eds-iit-checkbox/mixins/cryptoEdsKeys'
import EdsKeyData from '~/services/EdsKey/EdsKeyData'
import { cloudSignatureProviders } from '~/modules/eds-iit-checkbox/constants/cloudSignatureProviders'

export default {
  name: 'EdsIitCheckbox',
  components: {
    EdsIitCheckboxForm,
    EdsIitSavedKeys,
    QrcodeVue,
    EdsIitCheckboxKeyInfo,
    EProgressCircular,
    EdsIitCheckboxSignOptions
  },
  mixins: [edsIitCheckboxSteps, cryptoEdsKeys, responsive],
  props: {
    type: {
      type: String,
      default: 'edsIitCheckboxForOrganization'
    },
    showCachedDataMessage: {
      type: Boolean,
      default: false
    },
    onlyFileSign: {
      type: Boolean,
      default: false
    },
    keepBackBtnVisible: {
      type: Boolean,
      default: false
    },
    readDiiaKey: {
      type: Boolean,
      default: false
    },
    readMonoKey: {
      type: Boolean,
      default: false
    },
    onInterfaceActionError: {
      type: Function,
      default: () => {}
    },
    onConfirm: {
      type: Function,
      default: () => {}
    },
    titles: {
      type: Object,
      default: () => {}
    },
    saveIitInstanceAfterDialogClose: {
      type: Boolean,
      default: false
    },
    authorizeViaDiia: {
      type: Boolean,
      default: false
    },
    visibleSignOptions: {
      type: Array,
      default: null
    }
  },
  data: () => ({
    endUser: null,
    keyMedias: [],
    pkFormType: 1,
    textError: null,
    loading: false,
    initializeError: false,
    localKeyData: null,
    selectedOption: null,
    currentStep: 'options',
    backButtonDisabled: false,
    hideSavedKeysBtn: false,
    isStamp: false,
    selectedCloudProviderIndex: null,
    selectedCloudProviderName: null
  }),
  computed: {
    ...mapGetters('edsKeys', {
      fileData: 'uploadedKey',
      stampData: 'uploadedStamp',
      stampPassword: 'stampPassword'
    }),
    model() {
      return EdsIitCheckbox
    },
    backButtonDisabledComputed() {
      return Boolean(this.backButtonDisabled || this.diiaDeepLinkLoading)
    },
    showQrBlock() {
      return this.isCloudSign || this.isDiiaSign || this.isMonoSign
    },
    isDiiaSign() {
      return this.currentStep === this.steps.diiaRead || this.currentStep === this.steps.diiaSign
    },
    isMonoSign() {
      return this.currentStep === this.steps.monoSign || this.currentStep === this.steps.monoRead
    },
    isCloudSign() {
      return (
        this.currentStep === this.steps.smartIdRead ||
        this.currentStep === this.steps.smartIdSign ||
        this.currentStep === this.steps.cloudSignatureQrRead ||
        this.currentStep === this.steps.cloudSignatureQrSign
      )
    },
    backBtnVisible() {
      if (this.keepBackBtnVisible) {
        return true
      }
      if (this.currentStep === this.steps.fileSign && this.onlyFileSign) {
        return false
      }
      return this.currentStep !== this.steps.options
    },
    shownTitle() {
      let title = ''
      if (this.currentStep === this.steps.options) {
        title = 'Signature of the request to the DPS'
      } else if (this.currentStep === this.steps.keyInfo) {
        if (this.isStamp) {
          title = 'Check the read stamp information'
        } else {
          title = 'Check the read key information'
        }
      } else if (this.currentStep === this.steps.smartIdRead) {
        title = 'Read the key via SmartID'
      } else if (this.currentStep === this.steps.smartIdSign) {
        title = this.modelInstance?.title || 'Signature via SmartID'
      } else if (this.currentStep === this.steps.diiaRead) {
        title = 'Read the key through Diia.Signature'
      } else if (this.currentStep === this.steps.diiaSign) {
        title = this.modelInstance?.title || 'Signature via Diia.Signature'
      } else if (this.currentStep === this.steps.monoRead) {
        title = 'Read the key through Mono.Signature'
      } else if (this.currentStep === this.steps.monoSign) {
        title = this.modelInstance?.title || 'Sign via monoKEP'
      } else if (this.currentStep === this.steps.cloudSignatureQrRead) {
        title =
          this.modelInstance?.title ||
          this.$t('Read the key through {provider}', {
            provider: this.selectedCloudProviderName
          })
      } else if (this.currentStep === this.steps.cloudSignatureQrSign) {
        title =
          this.modelInstance?.title ||
          this.$t('Signature via {provider}', {
            provider: this.selectedCloudProviderName
          })
      } else {
        title = 'Sign with EDS key (KEP)'
      }
      return this._.get(this.titles, this.currentStep, null) || title
    },
    modelInstance() {
      return this.model.query().where('type', this.type).first()
    },
    qrUrl() {
      return this.modelInstance?.qrUrl || null
    },
    diiaDeepLinkLoading() {
      return this.modelInstance?.diiaDeepLinkLoading || false
    },
    pkFormTypes() {
      return {
        file: 1,
        KM: 2,
        KSP: 3
      }
    },
    isPkFormTypeFile() {
      return this.pkFormType === this.pkFormTypes.file
    },
    isPkFormTypeKM() {
      return this.pkFormType === this.pkFormTypes.KM
    },
    isPkFormTypeKSP() {
      return this.pkFormType === this.pkFormTypes.KSP
    },
    localShowCachedDataMessage() {
      const signInterface = this.modelInstance?.getInterface()
      return Boolean(
        signInterface &&
          !signInterface?.isSmartId &&
          !signInterface?.isDiia &&
          this.showCachedDataMessage
      )
    },
    settings() {
      return {
        language: 'uk',
        encoding: 'utf-8',
        httpProxyServiceURL: this.$config.iitProxyHandlerUrl,
        directAccess: true,
        CAs,
        CACertificates: `${this.$config.webURL}/CACertificates.p7b`,
        allowedKeyMediaTypes,
        KSPs: cloudSignatureProviders
      }
    },
    formCloudProviders() {
      // Exclude Privat cloud signature, because it is treated as by another flow in UI and cloudProviders is just for v-select
      return this._.filter(
        this.settings.KSPs,
        ksp => ksp.name !== 'Приватбанк - хмарний підпис "SmartID"'
      )
    },
    showSubtitle() {
      return (
        this.currentStep === this.steps.diiaSign ||
        this.currentStep === this.steps.monoSign ||
        this.currentStep === this.steps.smartIdRead
      )
    },
    subtitle() {
      if (this.currentStep === this.steps.diiaSign) {
        if (this.xsDevice) {
          return this.$t(
            'Recommendation: after signing in Diia app come back to document page and wait for signature apply'
          )
        } else {
          return this.$t('Scan QR-code and follow instructions in Diia app')
        }
      } else if (this.currentStep === this.steps.monoSign) {
        if (this.xsDevice) {
          return this.$t(
            'Recommendation: after signing in monobank app come back to document page and wait for signature apply'
          )
        } else {
          return this.$t('Scan QR-code and follow instructions in monobank app')
        }
      } else if (this.currentStep === this.steps.smartIdRead) {
        return this.$t('To sign the document, you need to log in to the Privat24 application')
      }
      return null
    },
    buttonLinkText() {
      if (this.currentStep === this.steps.diiaSign) {
        return this.$t('Diia.Sign')
      } else if (this.currentStep === this.steps.monoSign) {
        return this.$t('monoKEP')
      } else if (this.currentStep === this.steps.smartIdRead) {
        return this.$t('Open Privat')
      } else if (this.currentStep === this.steps.smartIdSign) {
        return this.$t('Sign with Privat')
      }
      return null
    },
    buttonLinkIcon() {
      if (
        this.currentStep === this.steps.smartIdRead ||
        this.currentStep === this.steps.smartIdSign
      ) {
        return 'privat24'
      } else if (this.currentStep === this.steps.monoSign) {
        return 'monobank'
      } else {
        return 'diia-1'
      }
    },
    isSmartIdOption() {
      return (
        this.currentStep === this.steps.smartIdRead || this.currentStep === this.steps.smartIdSign
      )
    },
    currentSmartIdStep() {
      return this.currentStep === this.steps.smartIdRead ? 1 : 2
    },
    totalSmartIdSteps() {
      return [this.steps.smartIdRead, this.steps.smartIdSign]
    },
    savedKeys() {
      if (!this.$User || !this.$User.id) {
        return []
      }
      return this._.get(this.$localStorageClientData, ['savedEdsKeys', this.$User.id]) || this._.get(this.$localStorageClientData, 'savedEdsKeys')
    },
    haveSavedKeys() {
      return (this.savedKeys && this.savedKeys.length) && !this.hideSavedKeysBtn
    },
    haveSavedKeysWithAuthBoolean() {
      return !!(this.haveSavedKeys && this.$User)
    },
    showSaveSwitch() {
      return !!(this.isPkFormTypeFile && !this.isStamp && this.$User)
    },
    shortenCloudSignatureProvidersName() {
      return {
        'Вчасно - хмарний підпис (QR)': 'Вчасно',
        'ТОВ «ЦСК «Україна» - хмарний підпис CloudKey': 'CloudKey',
        'АТ «ПУМБ» - хмарний підпис': 'Pumb',
        'Укргазбанк - хмарний підпис «EcoSign»': 'EcoSign',
        'Банк Альянс - хмарний підпис': 'Банк Альянс',
        'КНЕДП органів прокуратури - хмарний підпис': 'КНЕДП органів прокуратури',
        'Ощадбанк - хмарний підпис': 'Ощадбанк'
      }
    }
  },
  watch: {
    pkFormType() {
      if (this.selectedOption === this.steps.fileSign) {
        this.initialize()
      }
    },
    backButtonDisabledComputed: {
      immediate: true,
      handler(val) {
        this.$emit('backBtnDisabled', val)
      }
    }
  },
  created() {
    // to reset interface last selection
    this.model.deleteData()
    if (this.authorizeViaDiia) {
      this.handleOptionSelect(this.steps.diiaRead)
    }
  },
  beforeDestroy() {
    this.stopIntervalRequest()
    this.resetOperationKSP()
  },
  methods: {
    async handleQrCloudSignature(kspName) {
      try {
        this.selectedCloudProviderName = this.shortenCloudSignatureProvidersName[kspName]
        this.handleStepChange(this.steps.cloudSignatureQrRead)
        const initialized = await this.initialize()
        if (initialized) {
          await this.endUser.ReadPrivateKeyKSP('', kspName, true)
        }
        this.localKeyData = await this.endUser.GetOwnCertificates()
        this.handleStepChange(this.steps.keyInfo)
        this.saveModelData({ qrUrl: null })
      } catch (e) {
        if (e?.code !== 12) {
          this.$handlers.error(e, this)
        }
        await this.resetSavedData()
      }
    },
    handleBackBtnClick() {
      if (this.currentStep === this.steps.keyInfo) {
        this.handleStepChange(this.selectedOption || this.steps.options)
        if (this.selectedOption === this.steps.smartIdRead) {
          this.handleReadSmartIdKey()
        }
      } else {
        if (
          this.currentStep === this.steps.smartIdRead ||
          this.currentStep === this.steps.smartIdSign ||
          this.currentStep === this.steps.cloudSignatureQrRead ||
          this.currentStep === this.steps.cloudSignatureQrSign
        ) {
          this.resetOperationKSP()
        } else if (
          this.currentStep === this.steps.diiaRead ||
          this.currentStep === this.steps.diiaSign ||
          this.currentStep === this.steps.monoSign
        ) {
          this.stopIntervalRequest()
        }
        this.handleStepChange(this.steps.options)
      }
    },
    stopIntervalRequest() {
      if (this.modelInstance) {
        this.modelInstance.stopIntervalRequest()
      }
    },
    async resetOperationKSP() {
      if (this.endUser) {
        await this.endUser.ResetOperationKSP()
      }
      const signInterface = this.modelInstance?.getInterface()
      if (signInterface?.endUser) {
        await signInterface.endUser.ResetOperationKSP()
      }
    },
    // not used, key method of this function to sign if key data saved, but we do not save key data (might be useful someday)
    async onCreate() {
      const signInterface = this.modelInstance?.getInterface()
      if (signInterface) {
        if (signInterface?.isSmartId) {
          this.pkFormType = this.pkFormTypes.KSP
          this.selectedOption = this.steps.smartIdRead
        } else if (signInterface?.isDiia) {
          const nextStep = this.steps.diiaSign
          this.selectedOption = nextStep
          this.handleStepChange(nextStep)
        } else if (signInterface?.isMono) {
          const nextStep = this.steps.monoSign
          this.selectedOption = nextStep
          this.handleStepChange(nextStep)
        }
        await this.handleConfirm()
      } else if (this.onlyFileSign) {
        await this.handleOptionSelect(this.steps.fileSign)
      }
    },
    async handleOptionSelect(option) {
      this.selectedOption = option
      this.handleStepChange(option)
      if (option === this.steps.smartIdRead) {
        this.pkFormType = this.pkFormTypes.KSP
        const initialized = await this.initialize()
        if (initialized) {
          await this.handleReadSmartIdKey()
        }
      } else if (option === this.steps.diiaSign) {
        if (this.readDiiaKey) {
          this.selectedOption = this.steps.diiaRead
          this.handleStepChange(this.steps.diiaRead)
          this.saveModelData({
            title: null,
            qrUrl: null
          })
        }
        const initialized = await this.initialize()
        if (initialized) {
          if (this.readDiiaKey) {
            await this.handleReadDiiaKey()
          } else {
            await this.handleConfirm()
          }
        }
      } else if (option === this.steps.diiaRead && this.authorizeViaDiia) {
        this.selectedOption = this.steps.diiaRead
        this.handleStepChange(this.steps.diiaRead)
        this.saveModelData({
          title: null,
          qrUrl: null
        })
        const initialized = await this.initialize()
        if (initialized) {
          const authData = await this.handleReadDiiaKey(true)
          this.$emit('confirm', authData)
        }
      } else if (option === this.steps.monoSign) {
        if (this.readMonoKey) {
          this.selectedOption = this.steps.monoRead
          this.handleStepChange(this.steps.monoRead)
          this.saveModelData({
            title: null,
            qrUrl: null
          })
        }
        const initialized = await this.initialize()
        if (initialized) {
          if (this.readMonoKey) {
            await this.handleReadMonoKey()
          } else {
            await this.handleConfirm()
          }
        }
      } else {
        this.pkFormType = this.pkFormTypes.file
        await this.initialize()
      }
    },
    handleStepChange(step) {
      this.currentStep = step
      this.$emit('stepChange', step)
      this.backButtonDisabled = false
      if (step === this.steps.options) {
        this.selectedOption = null
        this.clearData()
        if (this.modelInstance) {
          this.modelInstance.delete()
        }
      }
    },
    async resetSavedData() {
      if (this.modelInstance) {
        await this.modelInstance.delete()
      }
      this.clearData()
      await this.initialize()
    },
    saveModelData(data = {}) {
      if (this.modelInstance) {
        this.modelInstance.saveModelData(data)
      }
    },
    clearData() {
      this.endUser = null
      this.textError = null
      this.localKeyData = null
      this.keyMedias = []
      this.currentStep = this.selectedOption || this.steps.options
      this.backButtonDisabled = false
      this.isStamp = false
      this.selectedCloudProviderName = null
      this.selectedCloudProviderIndex = null
      this.saveModelData({
        qrUrl: null,
        title: null
      })
    },
    async checkLibraryModules() {
      // Перевірка чи встановлені необхідні модулі для роботи криптографічної бібліотеки
      const result = await this.endUser.GetLibraryInfo()
      if (!result.supported) {
        this.textError = this.$t('The web signature library is not supported by your browser or OS')
        return false
      }
      if (!result.loaded) {
        // Бібліотека встановлена, але потребує оновлення
        if (result.isNativeLibraryNeedUpdate) {
          this.textError = `${this.$t('The web signature library needs updating. Please install the update by')}
            <a href="${result.nativeLibraryInstallURL}" target="_blank">${this.$t('link')}</a>`
          return false
        }
        // Якщо браузер підтримує web-розширення рекомендується
        // додатково до нативних модулів встановлювати web-розширення
        // Увага! Встановлення web-розширень ОБОВ'ЯЗКОВЕ для ОС Linux та ОС Windows Server
        if (result.isWebExtensionSupported && !result.isWebExtensionInstalled) {
          this.textError = `${this.$t('The web signature library requires the web extension to be installed. Please install the web extension by')}
            <a href="${result.webExtensionInstallURL}" target="_blank">${this.$t('link')}</a> ${this.$t('and refresh the page')}`
          return false
        }
        // Бібліотека (нативні модулі) не встановлені
        this.textError = `${this.$t('The web signature library needs to be installed. Please install the library by')}
            <a href="${result.nativeLibraryInstallURL}" target="_blank">${this.$t('link')}</a> ${this.$t('and refresh the page')}`
        return false
      }
      return true
    },
    async initialize() {
      try {
        this.loading = true
        this.initializeError = false
        if (!this.modelInstance) {
          await this.model.insert({
            data: {
              type: this.type,
              helpers: {
                axios: this.$axios,
                organization: this.$Organization
              }
            }
          })
        }
        if (this.isPkFormTypeFile || this.isPkFormTypeKSP) {
          const endUser = this.modelInstance?.getEndUser()
          const nextEndUser = endUser || new EndUser()
          this.endUser = nextEndUser
          if (!endUser) {
            this.modelInstance.saveData({ endUser: nextEndUser })
          }
        } else if (this.isPkFormTypeKM) {
          const endUserKM = this.modelInstance?.getEndUserKM()
          const nextEndUser = endUserKM || new EndUser(null, EndUserConstants.EndUserLibraryType.SW)
          this.endUser = nextEndUser
          if (!endUserKM) {
            this.modelInstance.saveData({ endUserKM: nextEndUser })
          }
          const readyToUse = await this.checkLibraryModules()
          if (!readyToUse) {
            return
          }
        }
        const initialized = await this.endUser.IsInitialized()
        if (!this.endUser) {
          return
        }
        if (!initialized) {
          await this.endUser.Initialize(this.settings)
        }
        await this.endUser.SetRuntimeParameter(
          EndUserConstants.EU_SIGN_TYPE_PARAMETER,
          EndUserConstants.EU_SIGN_TYPE_CADES_X_LONG
        )

        if (this.isPkFormTypeKM) {
          this.keyMedias = await this.endUser.GetKeyMedias()
        }

        if (this.isPkFormTypeKSP) {
          this.endUser.AddEventListener(
            EndUserConstants.EndUserEventType.ConfirmKSPOperation,
            e => {
              this.saveModelData({
                qrUrl: e?.url
              })
            }
          )
        }
        return true
      } catch (e) {
        this.initializeError = true
        this.$handlers.error(e, this)
      } finally {
        this.loading = false
      }
    },
    async handleReadMonoKey() {
      try {
        if (this.modelInstance) {
          const dataToBeSigned = [
            {
              content: 'Dubidoc',
              name: this.$t(this.shownTitle),
              isHashed: false
            }
          ]
          const signedData = await this.modelInstance.signListByMono(dataToBeSigned, {
            endUser: this.endUser
          })
          const signature = this._.get(signedData, '[0].signature', signedData)
          if (signature) {
            const keyData = await this.endUser.GetSigner(Base64js.toByteArray(signature), 0)
            this.localKeyData = [keyData]
            this.handleStepChange(this.steps.keyInfo)
            this.saveModelData({
              qrUrl: null
            })
          }
        }
      } catch (e) {
        this.$handlers.error(e, this)
        await this.resetSavedData()
      }
    },
    async handleReadDiiaKey(returnKeyData = false) {
      try {
        if (this.modelInstance) {
          const signedData = await this.modelInstance.signByDiia('Dubidoc', {
            endUser: this.endUser,
            title: this.$t(this.shownTitle),
            isHashed: false
          })
          if (signedData) {
            const keyData = await this.endUser.GetSigner(Base64js.toByteArray(signedData), 0)
            if (returnKeyData) {
              return new EdsKeyData([keyData])
            }
            this.localKeyData = [keyData]
            this.handleStepChange(this.steps.keyInfo)
            this.saveModelData({
              qrUrl: null
            })
          }
        }
      } catch (e) {
        this.$handlers.error(e, this)
        await this.resetSavedData()
      }
    },
    async handleReadSmartIdKey() {
      try {
        this.loading = true
        if (!this.endUser) {
          return
        }
        const ksp = this._.get(this.settings, 'KSPs[0].name')
        await this.endUser.ReadPrivateKeyKSP('', ksp, true)
        this.localKeyData = await this.endUser.GetOwnCertificates()
        this.handleStepChange(this.steps.keyInfo)
        this.saveModelData({
          qrUrl: null
        })
      } catch (e) {
        if (e?.code !== 12) {
          this.$handlers.error(e, this)
        }
        await this.resetSavedData()
      } finally {
        this.loading = false
      }
    },
    async handleReadEdsFileKey(keyObj, keyName, password) {
      try {
        this.loading = true
        const keyData = this._.get(keyObj, 'data')
        const fileData = new Uint8Array(keyData)
        const isJKSkey = keyName.endsWith('.jks')
        if (isJKSkey) {
          const jksKeys = await this.endUser.GetJKSPrivateKeys(fileData)
          const pkIndex = 0
          const pkCertificates = []
          for (let i = 0; i < jksKeys[pkIndex].certificates.length; i++) {
            pkCertificates.push(jksKeys[pkIndex].certificates[i].data)
          }
          await this.endUser.ReadPrivateKeyBinary(
            jksKeys[pkIndex].privateKey,
            password,
            pkCertificates,
            this.ca
          )
        } else {
          await this.endUser.ReadPrivateKeyBinary(fileData, password, null, this.ca)
        }
        this.localKeyData = await this.endUser.GetOwnCertificates()
        this.saveModelData({ qrUrl: null })
      } catch (e) {
        if (e?.code !== 12) {
          this.$handlers.error(e, this)
        }
        await this.resetSavedData()
      } finally {
        this.loading = false
      }
    },
    async handleReadStampKey() {
      const stampName = this._.get(this.stampData, 'file.name')
      await this.handleReadEdsFileKey(this.stampData, stampName, this.stampPassword)
      this.isStamp = true
    },
    async handleConfirm(shouldSaveKeyToLS, isStamp = false) {
      try {
        this.loading = true
        this.backButtonDisabled = true
        const isSmartId = this.isPkFormTypeKSP && this.selectedOption === this.steps.smartIdRead
        const isDiia =
          this.selectedOption === this.steps.diiaRead || this.selectedOption === this.steps.diiaSign
        const isCloudProvider =
          this.isPkFormTypeKSP && !this._.isNull(this.selectedCloudProviderIndex)
        const isMono =
          this.selectedOption === this.steps.monoRead || this.selectedOption === this.steps.monoSign
        const si = this.modelInstance.getInterface()
        if (!si?.confirmed || isStamp) {
          this.modelInstance.saveData({
            endUser: this.endUser,
            keyData: this.localKeyData,
            isSeal: !this._.get(this.localKeyData, '[0]infoEx.subjDRFOCode', ''),
            onInterfaceActionError: this.onInterfaceActionError,
            isSmartId,
            isDiia,
            isMono,
            isStamp,
            isCloudProvider,
            confirmed: true
          })
        }
        if (isSmartId) {
          this.handleStepChange(this.steps.smartIdSign)
        } else if (this.selectedCloudProviderName) {
          this.handleStepChange(this.steps.cloudSignatureQrSign)
        } else if (isDiia) {
          this.handleStepChange(this.steps.diiaSign)
        } else if (isMono) {
          this.handleStepChange(this.steps.monoSign)
        }
        const signInterface = this.modelInstance.getInterface()
        let onConfirmData
        if (this._.isFunction(this.onConfirm)) {
          try {
            onConfirmData = await this.onConfirm(signInterface)
          } catch (e) {
            // eslint-disable-next-line no-console
            console.log(e)
          }
        }
        if (shouldSaveKeyToLS) {
          this.saveKeyDataToLS()
        }
        // reads stamp key if key and stamp are being processed together
        if (this.stampData && !this.isStamp && !this.saveIitInstanceAfterDialogClose) {
          await this.handleReadStampKey()
          return
        }
        if (this.currentStep !== this.steps.options && signInterface?.confirmed) {
          this.$emit('confirm', {
            ...signInterface,
            onConfirmData,
            isEdsAndStampUploaded: !!(this.fileData && this.stampData),
            handleRereadStampKey: () =>
              this.handleReadEdsFileKey(
                this.stampData,
                this._.get(this.stampData, 'file.name'),
                this.stampPassword
              )
          })
        }
      } catch (e) {
        if (e?.code !== 12) {
          this.$handlers.error(e, this)
        }
        await this.resetSavedData()
      } finally {
        this.loading = false
        this.saveModelData({
          title: null,
          qrUrl: null
        })
      }
    },
    saveKeyDataToLS() {
      if (!this.$User || !this.$User.id) {
        return
      }
      const userId = this.$User.id
      const allSaved = this._.get(this.$localStorageClientData, 'savedEdsKeys', {}) || {}
      const userSaved = Array.isArray(allSaved[userId]) ? allSaved[userId] : []

      const keyInfo = {
        keyBase64: Base64js.fromByteArray(new Uint8Array(this._.get(this.fileData, 'data'))),
        fileName: this._.get(this.fileData, 'file.name', null),
        keyName: this._.get(this.localKeyData, '[0].infoEx.subjFullName'),
        endTime: this._.get(this.localKeyData, '[0].infoEx.certEndTime')
      }

      const hasKeyAlreadyStored = this._.some(userSaved, entry => {
        try {
          return this.decryptKeyInfo(entry).keyBase64 === keyInfo.keyBase64
        } catch (e) {
          return false
        }
      })

      if (!hasKeyAlreadyStored) {
        const encryptedData = this.encryptKeyInfo(JSON.stringify(keyInfo))
        this.$setLocalStorageClientData({
          savedEdsKeys: {
            ...allSaved,
            [userId]: [...userSaved, encryptedData]
          }
        })
      }

      this.hideSavedKeysBtn = false
    }
  }
}
</script>

<style lang="scss">
.eds-iit-checkbox {
  position: relative;

  &__back-btn {
    transform: rotate(180deg);
    margin-right: 5px;

    &[disabled] {
      opacity: 0.35;
    }
  }

  &__title {
    font-weight: 700;
    color: $black;
    font-size: 20px;
    line-height: 28px;
    margin-bottom: 18px;
    display: flex;
    align-items: center;

    @media (min-width: map-get($breakpoints, 'sm')) {
      font-size: 32px;
      margin-bottom: 32px;
      line-height: 40px;
    }
  }

  &__subtitle {
    color: rgba(0, 0, 0, 0.8);
    font-size: 14px;
    font-weight: 300;
    line-height: 20px;
    margin-bottom: 18px;

    @media (min-width: map-get($breakpoints, 'sm')) {
      margin-bottom: 32px;
    }
  }

  &__file-input-wrapper {
    padding: 16px;
    border: 2px dashed #f0f1f2;
    border-radius: 16px;
    margin-bottom: 32px;

    &--error {
      .eds-iit-checkbox {
        &__file-input-key-icon {
          background: $danger;
        }

        &__file-input-btn {
          border-color: $danger !important;
          color: $danger !important;
        }
      }
    }

    &--success {
      .eds-iit-checkbox {
        &__file-input-key-icon {
          background: $primary;
        }

        &__file-input-btn {
          border-color: $primary !important;
          color: $primary !important;
        }
      }
    }
  }

  &__file-input-content {
    gap: 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;

    @media (min-width: map-get($breakpoints, 'md')) {
      flex-wrap: nowrap;
    }
  }

  &__file-input-key-icon {
    padding: 12px;
    border-radius: 12px;
    background: #f0f1f2;
    width: 48px;
    min-width: 48px;
    height: 48px;
    min-height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__file-input-text {
    flex-grow: 1;
    overflow: hidden;

    @media (min-width: map-get($breakpoints, 'sm')) {
      max-width: 235px;
    }

    h3 {
      font-weight: 600;
      font-size: 14px;
      color: #000;
      margin-bottom: 4px;
      text-overflow: ellipsis;
      overflow: hidden;
    }

    p {
      font-weight: 300;
      font-size: 12px;
      color: rgba(0, 0, 0, 0.75);
      margin: 0;
      text-overflow: ellipsis;
      overflow: hidden;
    }
  }

  &__file-input-btn {
    width: 100%;
    max-width: 140px;
  }

  &__error {
    font-size: 12px;
    color: $danger;
    width: 100%;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 5px;

    .main-button {
      width: 100%;

      @media (min-width: map-get($breakpoints, 'xs')) {
        max-width: 180px;
      }
    }
  }

  &__radio-group {
    margin-top: 0;
    padding-top: 0;
    margin-bottom: 32px;

    &::v-deep {
      legend.v-label {
        font-size: 14px !important;
        padding-bottom: 16px;
        color: #161b25;
      }

      .v-radio:not(:last-child):not(:only-child) {
        margin-bottom: 13px;
      }
    }
  }

  &__select {
    margin-bottom: 32px !important;
  }

  &__text-error {
    color: #161b25;
    font-size: 14px;
    margin-bottom: 27px;
  }

  &__loader {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 36px;
    padding-bottom: 30px;

    p {
      text-align: center;
      margin: 0;
      font-size: 14px;
    }
  }

  &__stamp-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;

    padding: 32px 0;
  }

  &__qr-code {
    display: block;
    width: 100%;
    max-width: 300px;
    margin: 0 auto;
    text-decoration: none;
  }

  &__button-link {
    width: 100%;
    height: 48px;

    display: flex;
    align-items: center;
    justify-content: center;

    margin-top: 24px;

    border-radius: 12px;
    background: $text-dark;

    font-size: 14px;
    font-weight: 500;
    line-height: 22px;
    color: $white;

    &-icon {
      order: 1;
      margin-left: 8px;
    }
  }

  &__page-counter {
    margin-bottom: 8px;
    display: inline-block;
    padding: 2px 6px;

    border-radius: 6px;
    background: #f0f1f2;

    color: $dark;
    font-size: 11px;
    font-weight: 400;
    line-height: 16px;
    letter-spacing: 0.11px;
  }
}
</style>
